/**
 * useAttentionFeed Hook
 *
 * Manages the list of cameras shown in the Attention Feed section of the
 * CANvision Dashboard.  When a high- or critical-severity event (or incident)
 * arrives over the WebSocket, the corresponding camera is added to a
 * FIFO-bounded list.  The maximum number of cameras is persisted to
 * localStorage so the user's preference survives page refreshes.
 *
 * MVP D-3a: Each camera entry now tracks MULTIPLE events (events[] array).
 * When a second event arrives for the same camera with a different event_type,
 * it is appended rather than overwriting the previous event.  Events are
 * removed individually when their status changes away from 'new' (via
 * canvms:event-updated).  The tile derives highestSeverity and latestEventType
 * from the events array.
 *
 * This hook subscribes to `lastEvent`, `lastIncident`, and `lastEventUpdate`
 * from the shared CANvisionProvider context rather than creating its own
 * socket connection.  The Provider's single socket broadcasts events to all
 * consumers.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useCANvisionContext } from '../context/useCANvisionContext';
import { getStreamUrl } from 'api/canvms';
import type { VMSEventPayload, VMSIncidentCreatedPayload, VMSEventUpdatedPayload } from './useCANVMSSocket';
import type { AttentionCamera, AttentionEvent, UseAttentionFeedReturn } from '../types/canvision.types';

// Re-export types so existing consumers that import from this file still work.
export type { AttentionCamera, AttentionEvent, UseAttentionFeedReturn };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** localStorage key for the user's preferred camera count. */
const STORAGE_KEY = 'canvision:attention-feed-count';

/** Default number of cameras shown in the Attention Feed. */
const DEFAULT_MAX_CAMERAS = 4;

/** Severities that qualify for the Attention Feed (>= high). */
const ATTENTION_SEVERITIES: ReadonlySet<string> = new Set<string>(['high', 'critical']);

/** Event statuses that cause removal from the feed (anything other than 'new'). */
const REMOVAL_STATUSES: ReadonlySet<string> = new Set<string>([
  'acknowledged', 'dismissed', 'promoted',
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read the persisted maxCameras value from localStorage. */
function readMaxCameras(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      const parsed = Number(stored);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
  } catch {
    // localStorage may be unavailable (e.g. incognito quota exceeded).
  }
  return DEFAULT_MAX_CAMERAS;
}

/** Persist maxCameras to localStorage. */
function writeMaxCameras(value: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Silently ignore write failures.
  }
}

/**
 * Returns true when the severity string qualifies for the Attention Feed.
 */
function isHighSeverity(severity: string | null | undefined): severity is 'high' | 'critical' {
  return severity != null && ATTENTION_SEVERITIES.has(severity);
}

/**
 * Derive the highest severity from an events array.
 * 'critical' > 'high'.  Falls back to 'high' if array is empty (shouldn't happen).
 */
function deriveHighestSeverity(events: AttentionEvent[]): 'critical' | 'high' {
  for (const evt of events) {
    if (evt.severity === 'critical') return 'critical';
  }
  return 'high';
}

/**
 * Derive the latest event type from events array (most recent addedAt).
 * Falls back to empty string if array is empty (shouldn't happen).
 */
function deriveLatestEventType(events: AttentionEvent[]): string {
  if (events.length === 0) return '';
  let latest = events[0];
  for (let i = 1; i < events.length; i++) {
    if (events[i].addedAt > latest.addedAt) {
      latest = events[i];
    }
  }
  return latest.eventType;
}

/**
 * Derive the most recent addedAt from events array (for camera-level expiry).
 */
function deriveLatestAddedAt(events: AttentionEvent[]): Date {
  if (events.length === 0) return new Date();
  let latest = events[0].addedAt;
  for (let i = 1; i < events.length; i++) {
    if (events[i].addedAt > latest) {
      latest = events[i].addedAt;
    }
  }
  return latest;
}

/**
 * Recalculate derived fields on an AttentionCamera from its events array.
 */
function recalcDerived(cam: AttentionCamera): AttentionCamera {
  return {
    ...cam,
    highestSeverity: deriveHighestSeverity(cam.events),
    latestEventType: deriveLatestEventType(cam.events),
    addedAt: deriveLatestAddedAt(cam.events),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages the Attention Feed camera list.
 *
 * @example
 * ```tsx
 * const { cameras, maxCameras, setMaxCameras, dismissCamera, dismissEvent, clearAll } =
 *   useAttentionFeed();
 * ```
 */
export function useAttentionFeed(): UseAttentionFeedReturn {
  // -- Context ---------------------------------------------------------------
  const { sites, lastEvent, lastIncident, lastEventUpdate } = useCANvisionContext();

  // -- State -----------------------------------------------------------------
  const [cameras, setCameras] = useState<AttentionCamera[]>([]);
  const [maxCameras, setMaxCamerasState] = useState<number>(readMaxCameras);

  // Keep a ref so socket callbacks always see the latest maxCameras without
  // causing the socket hook to reconnect.
  const maxCamerasRef = useRef(maxCameras);
  useEffect(() => {
    maxCamerasRef.current = maxCameras;
  }, [maxCameras]);

  // -- 10-minute camera expiry -----------------------------------------------
  // Cameras auto-evict from the feed after 10 minutes so stale entries do not
  // linger indefinitely.  We check once per minute.  The camera-level addedAt
  // is derived from the most recent event's addedAt.

  const EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

  useEffect(() => {
    const interval = setInterval(() => {
      const cutoff = new Date(Date.now() - EXPIRY_MS);
      setCameras((prev) => {
        const filtered = prev.filter((c) => c.addedAt > cutoff);
        if (filtered.length !== prev.length) return filtered;
        return prev; // no change — avoid unnecessary re-render
      });
    }, 60_000); // check every minute
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build a site-name lookup map: siteId -> display_name || name
  const siteNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const site of sites) {
      map.set(site.id, site.display_name || site.name);
    }
    return map;
  }, [sites]);

  // Keep ref for site name map so callbacks always see latest
  const siteNameMapRef = useRef(siteNameMap);
  useEffect(() => {
    siteNameMapRef.current = siteNameMap;
  }, [siteNameMap]);

  // -- Stream URL fetching ---------------------------------------------------

  /**
   * Attempt to enrich an AttentionCamera with its stream URL.
   * Failures are silently ignored -- the tile will just show without video.
   */
  const enrichWithStream = useCallback(async (cam: AttentionCamera): Promise<AttentionCamera> => {
    try {
      const response = await getStreamUrl(cam.siteId, cam.cameraId);
      const info = response.data;
      return {
        ...cam,
        streamUrl: info.stream_url,
        streamType: info.type || 'hls',
      };
    } catch (err) {
      console.error('[useAttentionFeed] Failed to fetch stream URL for camera', cam.cameraId, err);
      return cam;
    }
  }, []);

  // -- Camera list mutation helpers ------------------------------------------

  /**
   * Add or update an event within a camera entry in the feed.
   *
   * - Deduplicates cameras by cameraId.
   * - Within a camera, deduplicates events by eventType:
   *   - Same type: updates existing event (refresh addedAt, detectionCount, severity)
   *   - Different type: appends new AttentionEvent
   * - Recalculates derived fields (highestSeverity, latestEventType, addedAt)
   * - Trims the list to maxCameras via FIFO (oldest entries removed first).
   *
   * @param cameraShell  Partial camera data (identity + display fields)
   * @param newEvent     The event to add or merge
   */
  const addEvent = useCallback(
    (cameraShell: Omit<AttentionCamera, 'events' | 'highestSeverity' | 'latestEventType'>, newEvent: AttentionEvent) => {
      // Build a full camera entry for enrichment (may be new or merged below)
      const shellForEnrich: AttentionCamera = {
        ...cameraShell,
        events: [newEvent],
        highestSeverity: newEvent.severity,
        latestEventType: newEvent.eventType,
      };

      // Fire-and-forget stream enrichment (only fetches if no streamUrl yet)
      enrichWithStream(shellForEnrich).then((enriched) => {
        setCameras((prev) => {
          const max = maxCamerasRef.current;

          // Check if camera already exists in the feed
          const existingIndex = prev.findIndex((c) => c.cameraId === enriched.cameraId);

          let next: AttentionCamera[];

          if (existingIndex !== -1) {
            // Camera exists — merge event into its events array
            const existing = prev[existingIndex];
            const eventIdx = existing.events.findIndex((e) => e.eventType === newEvent.eventType);

            let updatedEvents: AttentionEvent[];
            if (eventIdx !== -1) {
              // Same event type already tracked — update in place
              updatedEvents = existing.events.map((e, i) =>
                i === eventIdx
                  ? {
                      ...newEvent,
                      // Preserve higher severity if existing was higher
                      severity: e.severity === 'critical' || newEvent.severity === 'critical' ? 'critical' : 'high',
                      // Increment detection count (additive from merge window)
                      detectionCount: newEvent.detectionCount > 0 ? newEvent.detectionCount : e.detectionCount + 1,
                    }
                  : e,
              );
            } else {
              // Different event type — append
              updatedEvents = [...existing.events, newEvent];
            }

            const updatedCamera = recalcDerived({
              ...existing,
              // Preserve existing stream info, prefer enriched if available
              streamUrl: enriched.streamUrl || existing.streamUrl,
              streamType: enriched.streamType || existing.streamType,
              events: updatedEvents,
            });

            next = prev.map((c, i) => (i === existingIndex ? updatedCamera : c));
          } else {
            // New camera — create entry with single event, prepend (newest first)
            const newCamera = recalcDerived({
              ...enriched,
              events: [newEvent],
            });
            next = [newCamera, ...prev];
          }

          // Trim to maxCameras -- remove oldest (end of array) when exceeded
          if (next.length > max) {
            next = next.slice(0, max);
          }

          return next;
        });
      });
    },
    [enrichWithStream],
  );

  // -- WebSocket event handlers ----------------------------------------------

  const handleEvent = useCallback(
    (event: VMSEventPayload) => {
      if (!isHighSeverity(event.severity)) return;
      if (event.camera_id == null) return; // skip events without a camera

      const cameraShell: Omit<AttentionCamera, 'events' | 'highestSeverity' | 'latestEventType'> = {
        cameraId: event.camera_id,
        siteId: event.site_id,
        cameraName: event.camera_name || `Camera ${event.camera_id}`,
        siteName: siteNameMapRef.current.get(event.site_id) || `Site ${event.site_id}`,
        addedAt: new Date(),
      };

      const attentionEvent: AttentionEvent = {
        eventId: event.id,
        eventType: event.event_type,
        severity: event.severity as 'high' | 'critical',
        summary: event.summary ?? null,
        detectionCount: 1,
        addedAt: new Date(),
      };

      addEvent(cameraShell, attentionEvent);
    },
    [addEvent],
  );

  const handleIncidentCreated = useCallback(
    (payload: VMSIncidentCreatedPayload) => {
      if (!payload?.incident) {
        console.error('[useAttentionFeed] handleIncidentCreated received invalid payload:', payload);
        return;
      }
      const { incident, endpointId } = payload;
      if (!isHighSeverity(incident.severity)) return;

      // Require a real camera_id to populate the feed — without it we
      // cannot fetch the stream URL and the tile would show "No stream".
      if (incident.camera_id == null) {
        console.error('[useAttentionFeed] Incident payload missing camera_id, skipping:', incident.id);
        return;
      }

      const cameraShell: Omit<AttentionCamera, 'events' | 'highestSeverity' | 'latestEventType'> = {
        cameraId: incident.camera_id,
        siteId: endpointId,
        cameraName: incident.camera_name || 'Unknown Camera',
        siteName: incident.site_name || siteNameMapRef.current.get(endpointId) || `Site ${endpointId}`,
        addedAt: new Date(),
      };

      const attentionEvent: AttentionEvent = {
        eventId: incident.id,
        eventType: incident.incident_type,
        severity: incident.severity as 'high' | 'critical',
        summary: null,
        detectionCount: 1,
        addedAt: new Date(),
        incidentId: incident.id,
      };

      addEvent(cameraShell, attentionEvent);
    },
    [addEvent],
  );

  // -- Event status update handler (canvms:event-updated) --------------------
  // When an event's status changes away from 'new', remove it from the feed.
  // If the camera has no remaining events, remove the entire camera entry.

  const handleEventUpdate = useCallback(
    (update: VMSEventUpdatedPayload) => {
      if (!REMOVAL_STATUSES.has(update.status)) return; // only remove on status change away from 'new'

      setCameras((prev) => {
        // Find the camera that contains this eventId
        const camIdx = prev.findIndex((c) =>
          c.events.some((e) => e.eventId === update.id),
        );
        if (camIdx === -1) return prev; // event not in our feed

        const cam = prev[camIdx];
        const remainingEvents = cam.events.filter((e) => e.eventId !== update.id);

        if (remainingEvents.length === 0) {
          // No events left — remove the entire camera entry
          return prev.filter((_, i) => i !== camIdx);
        }

        // Events remain — recalculate derived fields
        const updatedCamera = recalcDerived({
          ...cam,
          events: remainingEvents,
        });

        return prev.map((c, i) => (i === camIdx ? updatedCamera : c));
      });
    },
    [],
  );

  // -- Context-based subscription (replaces duplicate socket connection) ------
  // React to `lastEvent` / `lastIncident` / `lastEventUpdate` changes from
  // the Provider's single shared socket connection.  We keep refs to the
  // handlers so the effect dependencies stay minimal and do not cause
  // stale-closure bugs.

  const handleEventRef = useRef(handleEvent);
  useEffect(() => { handleEventRef.current = handleEvent; }, [handleEvent]);

  const handleIncidentRef = useRef(handleIncidentCreated);
  useEffect(() => { handleIncidentRef.current = handleIncidentCreated; }, [handleIncidentCreated]);

  const handleEventUpdateRef = useRef(handleEventUpdate);
  useEffect(() => { handleEventUpdateRef.current = handleEventUpdate; }, [handleEventUpdate]);

  // When a new event arrives via context, process it.
  useEffect(() => {
    if (lastEvent) {
      handleEventRef.current(lastEvent);
    }
  }, [lastEvent]);

  // When a new incident arrives via context, process it.
  useEffect(() => {
    if (lastIncident) {
      handleIncidentRef.current(lastIncident);
    }
  }, [lastIncident]);

  // When an event status update arrives via context, process removal.
  useEffect(() => {
    if (lastEventUpdate) {
      handleEventUpdateRef.current(lastEventUpdate);
    }
  }, [lastEventUpdate]);

  // -- Public API ------------------------------------------------------------

  /** Update maxCameras and persist to localStorage. */
  const setMaxCameras = useCallback((n: number) => {
    const clamped = Math.max(1, n);
    setMaxCamerasState(clamped);
    writeMaxCameras(clamped);
    // Trim existing cameras if the new max is smaller
    setCameras((prev) => (prev.length > clamped ? prev.slice(0, clamped) : prev));
  }, []);

  /** Remove a single camera (all its events) from the feed. UI-only quick clear. */
  const dismissCamera = useCallback((cameraId: number) => {
    setCameras((prev) => prev.filter((c) => c.cameraId !== cameraId));
  }, []);

  /**
   * Remove a single event from a camera in the feed.
   * If the camera has no remaining events after removal, the camera entry
   * is also removed.  This is used by the CameraDetail modal for per-event
   * dismiss.
   */
  const dismissEvent = useCallback((cameraId: number, eventId: number) => {
    setCameras((prev) => {
      const camIdx = prev.findIndex((c) => c.cameraId === cameraId);
      if (camIdx === -1) return prev;

      const cam = prev[camIdx];
      const remainingEvents = cam.events.filter((e) => e.eventId !== eventId);

      if (remainingEvents.length === 0) {
        // No events left — remove the entire camera entry
        return prev.filter((_, i) => i !== camIdx);
      }

      // Events remain — recalculate derived fields
      const updatedCamera = recalcDerived({
        ...cam,
        events: remainingEvents,
      });

      return prev.map((c, i) => (i === camIdx ? updatedCamera : c));
    });
  }, []);

  /** Remove all cameras from the feed. */
  const clearAll = useCallback(() => {
    setCameras([]);
  }, []);

  return {
    cameras,
    maxCameras,
    setMaxCameras,
    dismissCamera,
    dismissEvent,
    clearAll,
  };
}

export default useAttentionFeed;
